import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Screen } from "../components/Screen";
import { Ref } from "../components/Ref";
import { Tag } from "../components/Tag";
import { Reveal } from "../components/Reveal";
import { Segmented } from "../components/Segmented";
import { SkinBloodIllustration, type Compartment } from "../illustrations/SkinBloodIllustration";
import { useSnak } from "../snak/SnakContext";
export function P3Patients() {
  const {
    explore
  } = useSnak();
  const [focus, setFocus] = useState<Compartment | null>(null);
  return <Screen overline="Why it matters to patients" refTarget={5} footnote={<p data-id="element-459" data-mp="2kofsy0shrg0m">
          <span className="font-semibold text-[var(--s-fg)]" data-id="element-460" data-mp="2knwn3ig48i2y">Interpretation.</span> In a chronic, relapsing disease, durable
          disease control and longer intervals between treatment changes are clinically meaningful goals. MINT describes such
          outcomes; it does not show that they were caused by treatment. Schematic illustration, not to scale.
        </p>} data-id="element-458" data-mp="2gn4dgo3c0b65">
      <Reveal delay={0.1} data-id="element-461" data-mp="1treclz3obtma">
        <h1 className="mt-5 font-display text-[32px] font-medium leading-[1.1] tracking-[-0.01em]" data-id="element-462" data-mp="1ts7m37u0ox84">
          Beyond the numbers are patients living with{" "}
          <em className="font-medium text-[var(--s-accent)]" data-id="element-463" data-mp="27ouk7dxphtfm">MF and SS.</em>
        </h1>
      </Reveal>

      <Reveal delay={0.2} data-id="element-464" data-mp="2hpdaaqfv665z">
        <div className="-mx-6 mt-6" data-id="element-465" data-mp="2hottxe5v3pu2">
          <SkinBloodIllustration focus={focus} data-id="element-466" data-mp="2hnsc8qucc53d" />
        </div>
      </Reveal>

      <Reveal delay={0.8} data-id="element-467" data-mp="2cp8u71ivbdga">
        <div className="mt-4 flex items-center justify-between gap-3" data-id="element-468" data-mp="2cq0dbcva3ipo">
          <p className="overline text-[10px] text-[var(--s-muted)]" data-id="element-469" data-mp="2re50at1d6ge9">Explore</p>
          <Segmented size="lg" options={[{
          value: "skin",
          label: "SKIN"
        }, {
          value: "blood",
          label: "BLOOD"
        }]} value={focus} onChange={v => {
          setFocus(v as Compartment);
          explore();
        }} data-id="element-470" data-mp="2mfjywqadlqo8" />
        </div>

        <div className="mt-5 min-h-[132px]" data-id="element-471" data-mp="1t56q1x3tizca">
          <AnimatePresence mode="popLayout" initial={false} data-id="element-472" data-mp="1y1x4kfzz5mvl">
            <motion.div key={focus ?? "none"} initial={{
            opacity: 0,
            y: 4
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0
          }} transition={{
            duration: 0.25,
            ease: [0.23, 1, 0.32, 1]
          }} data-id="element-473" data-mp="1y0ca0qdnu3xq">
              {focus === "skin" && <>
                  <Tag kind="context" data-id="element-474" data-mp="2gsoat8oo0x5r" />
                  <p className="mt-2 text-[16px] leading-relaxed" data-id="element-475" data-mp="2grn0x7jdygpm">
                    The primary site of MF. Lesions range from patches and plaques to tumours and erythroderma. Pruritus,
                    pain and visible disease can substantially affect sleep, daily function and quality of life.
                  </p>
                </>}
              {focus === "blood" && <>
                  <Tag kind="context" data-id="element-476" data-mp="vfv9h3nt56yp" />
                  <p className="mt-2 text-[16px] leading-relaxed" data-id="element-477" data-mp="nvssk78iqeqg">
                    Malignant T cells can circulate in the peripheral blood. Significant blood involvement, together with
                    erythroderma, defines Sézary syndrome, and the blood compartment is formally staged as B0, B1 or B2.
                    <Ref n={5} data-id="element-478" data-mp="a5gvrv0znrsz" />
                  </p>
                </>}
              {focus === null && <p className="text-[16px] leading-relaxed text-[var(--s-muted)]" data-id="element-479" data-mp="16tn8z6bz7siv">
                  MF and SS are chronic lymphomas of skin-homing T cells, and can involve more than one compartment. Patients
                  often move through several systemic therapies over time.
                </p>}
            </motion.div>
          </AnimatePresence>
        </div>
      </Reveal>
    </Screen>;
}